import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import ProductImage from "components/source/shared/product-image";
import {
  armsShouldersFit,
  chestFit,
  lengthFit,
  overallFit,
  qualityIssueOptionsWithCopy,
  waistFit,
  wornHappinessLevel,
} from "constants/happiness-survey-values";

import {
  ProductImageOrientation,
  ProductImageSize,
  getProductImageAltText,
  getProductImageUrlsByOrientation,
} from "helpers/product-image-helpers";
import ProductImagePath from "helpers/product-image-path";
import Arrow from "images/left-arrow.svg";
import FeedbackModal from "./feedback/feedback-modal";
import RtrButton from "components/design-system/rtr-button";

const HappinessSurveyFeedback = ({ onBack }) => {
  const itemSurveys = useSelector(state => state.happinessSurvey?.itemSurveys || []);
  const isMobile = useSelector(state => state.browser?.isMobileViewport);

  const [modalState, setModalState] = React.useState({
    open: false,
    currentSurveyIndex: 0,
    feedbackType: "",
  });

  const openFeedbackModal = React.useCallback((feedbackType, currentSurveyIndex) => {
    setModalState({ open: true, currentSurveyIndex, feedbackType });
  }, []);

  const closeFeedbackScreen = React.useCallback(() => {
    setModalState(prev => ({ ...prev, open: false }));
  }, []);

  const issueReported = index =>
    itemSurveys[index]?.comment ||
    itemSurveys[index]?.surveyFeedbacks?.some(feedback =>
      Object.keys(qualityIssueOptionsWithCopy).includes(feedback.feedback)
    );

  const fitRatingPresent = index =>
    itemSurveys[index]?.surveyFeedbacks?.some(
      feedback =>
        Object.keys(wornHappinessLevel).includes(feedback.feedback) ||
        Object.keys(overallFit).includes(feedback.feedback) ||
        Object.keys(armsShouldersFit).includes(feedback.feedback) ||
        Object.keys(chestFit).includes(feedback.feedback) ||
        Object.keys(waistFit).includes(feedback.feedback) ||
        Object.keys(lengthFit).includes(feedback.feedback)
    );

  const fallBackThumbnailImgSrc = productData =>
    productData?.images?.[ProductImageOrientation.NO_MODEL.path]
      ? ProductImagePath + productData.images[ProductImageOrientation.NO_MODEL.path]?.[ProductImageSize.x480.path]
      : ProductImagePath + productData?.images?.[ProductImageOrientation.FRONT.path]?.[ProductImageSize.x480.path];

  if (!itemSurveys.length) return null;

  return (
    <>
      <div data-test-id="happiness-survey-feedback" className="px-4 relative">
        {isMobile || !modalState.open ? (
          <>
            <div className="relative flex justify-center items-center py-6">
              <button
                onClick={onBack}
                data-test-id="happiness-survey-back-button"
                className="absolute left-0 top-1/2 transform -translate-y-1/2">
                <Arrow />
              </button>
              <h2 className="text-h4">Share your feedback</h2>
            </div>

            <p className="text-center mb-4">
              Share a photo, leave a review, or let us know how it fit to help others find the right fit and feel.
            </p>

            <hr className="mb-4 border-lighterGray" />
            <div className="space-y-4">
              {itemSurveys.map((item, index) => (
                <div
                  key={item.bookingId}
                  className="flex items-start flex-col border border-lightGray rounded-lg p-3 bg-white">
                  <div className="flex items-start">
                    <ProductImage
                      className="rounded-lg w-20 aspect-2/3 mr-4 flex-shrink-0"
                      altText={getProductImageAltText(item?.product?.displayName, item?.product?.designer?.displayName)}
                      imageGroup={getProductImageUrlsByOrientation(item?.product?.images, [
                        ProductImageOrientation.NO_MODEL.path,
                      ])}
                      imageSize={ProductImageSize.x183}
                      fallbackSrc={fallBackThumbnailImgSrc(item?.product)}
                    />
                    <div className="flex-1">
                      <div className="flex items-start gap-3">
                        <span className="text-onyx">{item.product?.designer?.displayName}</span>
                        <span>|</span>
                        <span className="text-darkGray">Size {item?.size}</span>
                      </div>
                      <button onClick={() => openFeedbackModal("quality-issues", index)} className="underline mt-2">
                        {issueReported(index) ? "Edit Issue" : "Report An Issue"}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-start flex-row gap-3 mt-4 w-full">
                    <RtrButton
                      onClick={() => openFeedbackModal("fit-rate", index)}
                      className="!h-7 font-medium"
                      dataTestId="fit-rate-btn"
                      style="secondary"
                      fullWidth>
                      {fitRatingPresent(index) ? "Edit Fit Rating" : "Add Fit Rating"}
                    </RtrButton>

                    <RtrButton
                      onClick={() => {}}
                      fullWidth
                      dataTestId="add-photo-btn"
                      style="primary"
                      className="!h-7 font-medium">
                      {issueReported(index) ? "Edit Photo" : "Add Photo"}
                    </RtrButton>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>
      <FeedbackModal
        isOpen={modalState.open}
        currentSurveyIndex={modalState.currentSurveyIndex}
        closeFeedbackScreen={closeFeedbackScreen}
        feedBackType={modalState.feedbackType}
      />
    </>
  );
};

HappinessSurveyFeedback.propTypes = {
  onBack: PropTypes.func,
};

export default HappinessSurveyFeedback;
